import { Accordion } from '@/modules/common/components/Accordion';
import { AccordionAddIconButton } from '@/modules/common/components/AccordionAddIconButton';
import { Stack } from '@mui/system';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlowStops } from './FlowStops';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppStore } from '@/modules/app/store/useAppStore';

interface Props {
  selectedIds: string[];
}

function FlowStopPanelComponent({ selectedIds }: Props) {
  const { t } = useTranslation('interface');
  const [open, setOpen] = useState<boolean>(false);
  const [showDraft, setShowDraft] = useState<boolean>(false);

  const mode = useAppStore((state) => state.mode);
  const flowStops = useFloorplanDesignStore((state) => state.design.flows.flowStops);
  const areas = useFloorplanDesignStore((state) => state.design.areas);

  const isDisabled = mode !== 'EDITABLE';

  const handleSetOpen = useCallback((expanded: boolean) => {
    setOpen(expanded);
  }, []);

  const handleAdd = useCallback(() => {
    if (isDisabled) return;

    setShowDraft(true);
    setOpen(true);
  }, [isDisabled]);

  const handleDismissDraft = useCallback(() => {
    setShowDraft(false);
  }, []);

  return (
    <Stack px={2} pt={1} spacing={1}>
      <Accordion
        title={t('interface:properties.flow_stops.title', 'Flow Stops')}
        expanded={open}
        onChange={handleSetOpen}
        afterIcon={
          <AccordionAddIconButton
            tooltip={t('interface:properties.flow_stops.add_tooltip', 'Add flow stop')}
            onAdd={handleAdd}
            tooltipWhenDisabled={false}
            disabled={isDisabled || showDraft}
          />
        }
      >
        <FlowStops
          disabled={isDisabled}
          flowStops={flowStops}
          areas={areas}
          selectedIds={selectedIds}
          showDraft={showDraft}
          onDismissDraft={handleDismissDraft}
        />
      </Accordion>
    </Stack>
  );
}

export const FlowStopPanel = memo(FlowStopPanelComponent);
